import { StorageKind, type CollectionNode, type LocationNode } from '@numistic/contracts'
import { useCreateNode, useRemoveNode, useTree, useUpdateNode } from '@numistic/data'
import { useTranslation } from '@numistic/i18n'
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Field,
  Input,
  Select,
  Textarea,
} from '@numistic/ui'
import { Link } from '@tanstack/react-router'
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

import { MutationErrors } from '../components/mutation-errors.js'
import { TreeView } from './tree-view.js'

type Kind = 'collections' | 'locations'
type Node = (CollectionNode | LocationNode) & { isInbox?: boolean; kind?: StorageKind }

/** Путь узла: «Банкноты / По странам / Германия». */
function pathOf(nodes: Node[], id: string): string {
  const byId = new Map(nodes.map((n) => [n.id, n]))
  const parts: string[] = []
  for (let n = byId.get(id); n; n = n.parentId ? byId.get(n.parentId) : undefined)
    parts.unshift(n.name)
  return parts.join(' / ')
}

/** id узла и всех вложенных — их нельзя сделать родителем (цикл). */
function subtree(nodes: Node[], id: string): Set<string> {
  const out = new Set([id])
  let grew = true
  while (grew) {
    grew = false
    for (const n of nodes)
      if (n.parentId && out.has(n.parentId) && !out.has(n.id)) {
        out.add(n.id)
        grew = true
      }
  }
  return out
}

/**
 * Управление деревом коллекций или мест хранения: слева дерево (перетаскивание — перенос),
 * справа — выбранный узел: имя, описание или вид, родитель, вложенные, удаление.
 */
export function TreeManager({
  kind,
  details,
}: {
  kind: Kind
  /** Доп. блок под карточкой узла (ссылки доступа, экземпляры в месте хранения). */
  details?: (node: Node) => ReactNode
}) {
  const { t } = useTranslation()
  const tree = useTree(kind)
  const create = useCreateNode(kind)
  const update = useUpdateNode(kind)
  const remove = useRemoveNode(kind)
  const nodes = useMemo(() => (tree.data ?? []) as Node[], [tree.data])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const selected = nodes.find((n) => n.id === selectedId) ?? null
  const [rootName, setRootName] = useState('')
  const labels = kind === 'collections' ? 'tree.collections' : 'tree.locations'
  const panel = useRef<HTMLDivElement>(null)

  // На телефоне карточка узла под деревом — после выбора прокручиваем к ней.
  useEffect(() => {
    if (selectedId && window.matchMedia?.('(max-width: 1023px)').matches)
      panel.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [selectedId])

  const addNode = (parentId: string | null, name: string, onDone?: () => void) => {
    if (!name.trim()) return
    create.mutate(
      {
        name: name.trim(),
        parentId,
        ...(kind === 'locations' ? { kind: 'album' as StorageKind } : {}),
      },
      {
        onSuccess: (n) => {
          setSelectedId(n.id)
          onDone?.()
        },
      },
    )
  }

  return (
    <div className="grid items-start gap-6 lg:grid-cols-[20rem_minmax(0,1fr)]">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t(`${labels}.title`)}</CardTitle>
          <p className="text-xs text-muted-foreground">{t('tree.dragHint')}</p>
        </CardHeader>
        <CardContent className="grid gap-4">
          {tree.isPending ? (
            <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
          ) : nodes.length ? (
            <TreeView
              nodes={nodes}
              rootLabel={t(`${labels}.root`)}
              selectedId={selectedId}
              onSelect={setSelectedId}
              onMove={(id, parentId) => {
                const node = nodes.find((n) => n.id === id)
                if (node) update.mutate({ id, version: node.version, parentId })
              }}
            />
          ) : (
            <p className="text-sm text-muted-foreground">{t(`${labels}.empty`)}</p>
          )}
          <form
            className="flex gap-2 border-t pt-4"
            onSubmit={(e) => {
              e.preventDefault()
              addNode(null, rootName, () => setRootName(''))
            }}
          >
            <Input
              aria-label={t(`${labels}.newRoot`)}
              placeholder={t(`${labels}.newRoot`)}
              value={rootName}
              onChange={(e) => setRootName(e.target.value)}
            />
            <Button type="submit" variant="outline" disabled={create.isPending || !rootName.trim()}>
              {t('common.add')}
            </Button>
          </form>
          <MutationErrors errors={[create.error, update.error, remove.error]} />
        </CardContent>
      </Card>

      {selected ? (
        <div ref={panel} className="grid scroll-mt-16 gap-6">
          <NodeEditor
            key={`${selected.id}:${selected.version}`}
            kind={kind}
            node={selected}
            nodes={nodes}
            pending={update.isPending || remove.isPending || create.isPending}
            onSave={(patch) =>
              update.mutate({ id: selected.id, version: selected.version, ...patch })
            }
            onAddChild={(name, done) => addNode(selected.id, name, done)}
            onRemove={() =>
              remove.mutate(selected.id, { onSuccess: () => setSelectedId(selected.parentId) })
            }
          />
          {details?.(selected)}
        </div>
      ) : (
        <p className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground">
          {t(`${labels}.pick`)}
        </p>
      )}
    </div>
  )
}

function NodeEditor({
  kind,
  node,
  nodes,
  pending,
  onSave,
  onAddChild,
  onRemove,
}: {
  kind: Kind
  node: Node
  nodes: Node[]
  pending: boolean
  onSave: (patch: {
    name?: string
    parentId?: string | null
    description?: string | null
    kind?: StorageKind
  }) => void
  onAddChild: (name: string, done: () => void) => void
  onRemove: () => void
}) {
  const { t } = useTranslation()
  const labels = kind === 'collections' ? 'tree.collections' : 'tree.locations'
  const [name, setName] = useState(node.name)
  const [description, setDescription] = useState(
    'description' in node ? (node.description ?? '') : '',
  )
  const [storageKind, setStorageKind] = useState<StorageKind>(node.kind ?? 'album')
  const [parentId, setParentId] = useState(node.parentId ?? '')
  const [child, setChild] = useState('')
  const blocked = subtree(nodes, node.id)
  const parents = nodes
    .filter((n) => !blocked.has(n.id))
    .map((n) => ({ id: n.id, label: pathOf(nodes, n.id) }))
    .sort((a, b) => a.label.localeCompare(b.label, 'ru'))
  const own = node.count
  const changed =
    name.trim() !== node.name ||
    (parentId || null) !== node.parentId ||
    (kind === 'collections' &&
      description !== ('description' in node ? (node.description ?? '') : '')) ||
    (kind === 'locations' && storageKind !== node.kind)

  return (
    <Card>
      <CardHeader className="gap-1">
        <CardTitle className="text-base">{pathOf(nodes, node.id)}</CardTitle>
        <p className="text-xs text-muted-foreground">{t('tree.ownCount', { count: own })}</p>
      </CardHeader>
      <CardContent className="grid gap-5">
        <form
          className="grid gap-4 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault()
            if (!name.trim()) return
            onSave({
              name: name.trim(),
              parentId: parentId || null,
              ...(kind === 'collections' ? { description: description.trim() || null } : {}),
              ...(kind === 'locations' ? { kind: storageKind } : {}),
            })
          }}
        >
          <Field
            label={t('tree.name')}
            className={kind === 'collections' ? 'sm:col-span-2' : undefined}
          >
            <Input value={name} onChange={(e) => setName(e.target.value)} required />
          </Field>
          {kind === 'locations' ? (
            <Field label={t('collections.locationKind')}>
              <Select
                value={storageKind}
                onChange={(e) => setStorageKind(e.target.value as StorageKind)}
              >
                {StorageKind.options.map((k) => (
                  <option key={k} value={k}>
                    {t(`tree.kinds.${k}`)}
                  </option>
                ))}
              </Select>
            </Field>
          ) : null}
          <Field label={t('tree.parent')} className="sm:col-span-2">
            <Select
              value={parentId}
              disabled={node.isInbox}
              onChange={(e) => setParentId(e.target.value)}
            >
              <option value="">{t(`${labels}.root`)}</option>
              {parents.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </Select>
          </Field>
          {kind === 'collections' ? (
            <Field label={t('tree.description')} className="sm:col-span-2">
              <Textarea
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Field>
          ) : null}
          <div className="flex flex-wrap gap-2 sm:col-span-2">
            <Button type="submit" disabled={pending || !changed || !name.trim()}>
              {t('common.save')}
            </Button>
            {kind === 'collections' ? (
              <Button asChild variant="outline">
                <Link to="/c" search={{ collection: node.id }}>
                  {t('tree.collections.open')}
                </Link>
              </Button>
            ) : null}
            {node.isInbox ? null : (
              <Button
                type="button"
                variant="ghost"
                className="ml-auto text-destructive"
                disabled={pending}
                onClick={() => {
                  if (window.confirm(t(`${labels}.deleteConfirm`, { name: node.name }))) onRemove()
                }}
              >
                {t('common.remove')}
              </Button>
            )}
          </div>
        </form>
        <form
          className="flex gap-2 border-t pt-4"
          onSubmit={(e) => {
            e.preventDefault()
            onAddChild(child, () => setChild(''))
          }}
        >
          <Input
            aria-label={t('tree.newChild')}
            placeholder={t('tree.newChild')}
            value={child}
            onChange={(e) => setChild(e.target.value)}
          />
          <Button type="submit" variant="outline" disabled={pending || !child.trim()}>
            {t('common.add')}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}
